/**
 * ToolCallTrace — collapsed Pill chip; expand toggles a Card showing each
 * tool call row with name + summary + Check icon (Plan 05-08a).
 *
 * UI-SPEC §Component 4. Collapsed by default; tap toggles expanded.
 * AnimatePresence height 200ms (instant under reduced-motion).
 *
 * #158 (steg 8a, Stell_Assistent): brikken er omrisset (secondary-line) med
 * listeikonet, 36 px høy inne i en 44 px treffflate. Utfoldet er det et
 * glasskort der hver rad har en grønn hake foran navnet og resultatet etter.
 *
 * data-testid: assistant-tool-trace, assistant-tool-trace-toggle,
 *              assistant-tool-row-{toolName}
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { Icon } from '../../components/primitives';
import { GLASS_SURFACE } from '../../components/primitives/Card';

export interface ToolCallSummary {
  id: string;
  name: string;
  duration_ms?: number;
  result_summary?: string;
  status: 'pending' | 'done';
}

export function ToolCallTrace({ toolCalls }: { toolCalls: ToolCallSummary[] }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (toolCalls.length === 0) return null;

  return (
    <div data-testid="assistant-tool-trace" className="flex flex-col items-start">
      <button
        type="button"
        data-testid="assistant-tool-trace-toggle"
        aria-expanded={expanded}
        className="flex min-h-11 items-center"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="inline-flex h-9 items-center gap-1.5 rounded-pill border border-secondary-line px-3 text-xs font-bold text-ink-2">
          <Icon.ToolList size={14} />
          {expanded
            ? t('assistant.tool_trace.collapse')
            : t('assistant.tool_trace.toggle', { count: toolCalls.length })}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
            className="w-full"
          >
            <ul className={`${GLASS_SURFACE} mt-1 flex flex-col gap-1.5 rounded-md px-3.5 py-2.5`}>
              {toolCalls.map((tc) => (
                <li
                  key={tc.id}
                  data-testid={`assistant-tool-row-${tc.name}`}
                  className="flex items-center gap-2 text-sm"
                >
                  <Icon.Check size={14} className="shrink-0 text-good" />
                  <span className="min-w-0 flex-1 font-bold text-ink">
                    {t(`assistant.tool.${_shortName(tc.name)}`, {
                      defaultValue: tc.name,
                    })}
                  </span>
                  <span className="shrink-0 text-ink-2">
                    {tc.result_summary ?? t('assistant.tool_trace.ok')}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Map full tool names to their i18n short keys */
function _shortName(toolName: string): string {
  if (toolName.startsWith('get_points')) return 'points';
  if (toolName === 'add_to_shopping_list') return 'add_shopping';
  if (toolName === 'get_chores') return 'chores';
  if (toolName === 'get_shopping') return 'shopping';
  if (toolName === 'get_weather') return 'weather';
  if (toolName === 'get_calendar') return 'calendar';
  if (toolName === 'get_streaks') return 'streaks';
  if (toolName === 'get_achievements') return 'achievements';
  return toolName.replace(/^get_/, '');
}
